import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import i18n from 'i18n-js';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { Alert, JOB_STATUSES } from '@helpers';
import { finishProductionOperation } from '@reducers/v2/planboard/thunks';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { useSelector } from 'react-redux';
import { selectMachines } from '@reducers/machines';
import { FINISH_ON_ACTIVE_BATCH_ALERT, MAX_QUANTITY, MAX_TIME_SECONDS } from '@v2/components/FinishJobModal/constants';
import { fetchAllBatches, aggregateBatchTimeData, resolveActualTimePrefill, getProvenanceText, getSetupTimeRows, getCycleTimeRows } from '@v2/components/FinishJobModal/helpers';
import { getSubmitValidation, showErrorAlert, showWarningChain } from '@v2/components/FinishJobModal/helpers/finishJobValidation';
import type { AggregatedBatchTimeData, FinishJobPayload, PrefillSource, TimeRowData, UseManageFinishJobModalParams } from '@v2/components/FinishJobModal/types';

const useManageFinishJobModal = ({
  item,
  machineId,
  hide,
  hasNote,
  noteDetailsText,
  editorMode,
  uploadNote,
  noteTouched,
  clearDraft,
  originScreen = TRACKING_EVENTS.PLANBOARD,
  onFinishSuccess,
}: UseManageFinishJobModalParams) => {
  const dispatch = useAppDispatch();
  const machines = useSelector(selectMachines) as Record<string, unknown>[];

  // --- Operation-level fallbacks ---
  // For an un-batched job the operation-level scheduled time IS the ERP "Paid" estimate. Both the
  // Paid row and the prefilled Actual derive from it (no batch statistics exist for un-batched jobs).
  const operationPaidSetup = item.setupTimeSeconds ?? 0;
  const operationPaidCycle = Math.max(0, (item.totalTimeScheduledSeconds ?? 0) - (item.setupTimeSeconds ?? 0));
  const fallbackSetupScheduled = operationPaidSetup;
  const fallbackCycleScheduled = operationPaidCycle;
  const initialSetupActual = operationPaidSetup;
  const initialCycleActual = operationPaidCycle;

  // --- Form state ---
  const [producedQuantity, setProducedQuantity] = useState<number>(item.quantityTarget ?? 0);
  const [actualEndDate, setActualEndDate] = useState<Date>(new Date());
  const [actualSetupTimeInSeconds, setActualSetupTimeInSeconds] = useState<number>(initialSetupActual);
  const [actualCycleTimeTotalInSeconds, setActualCycleTimeTotalInSeconds] = useState<number>(initialCycleActual);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // --- Batch data state ---
  const [batchTimeData, setBatchTimeData] = useState<AggregatedBatchTimeData | null>(null);
  const [isBatchDataLoading, setIsBatchDataLoading] = useState<boolean>(true);
  const [batchFetchError, setBatchFetchError] = useState<boolean>(false);
  const [hasRunningBatch, setHasRunningBatch] = useState<boolean>(false);

  // --- Manual edit guards ---
  const [hasEditedActualSetup, setHasEditedActualSetup] = useState<boolean>(false);
  const [hasEditedActualCycle, setHasEditedActualCycle] = useState<boolean>(false);

  // Track prefill source for provenance text
  const prefillSourceRef = useRef<PrefillSource>('operation');
  const abortControllerRef = useRef<AbortController | null>(null);

  // --- Batch data fetching ---
  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const loadBatchData = async () => {
      try {
        const allBatches = await fetchAllBatches(dispatch, item.productionOperationId, abortController.signal);
        if (abortController.signal.aborted) return;

        // An active linked batch keeps the job Running after finish (batch status overrides ERP).
        setHasRunningBatch(allBatches.some((b) => b.isInProgress));

        const aggregated = aggregateBatchTimeData(allBatches);
        setBatchTimeData(aggregated);

        if (aggregated) {
          const { setup, cycle, source } = resolveActualTimePrefill(aggregated, {
            setup: initialSetupActual,
            cycle: initialCycleActual,
          });
          prefillSourceRef.current = source;

          if (!hasEditedActualSetup) setActualSetupTimeInSeconds(setup);
          if (!hasEditedActualCycle) setActualCycleTimeTotalInSeconds(cycle);
        } else {
          prefillSourceRef.current = 'operation';
        }
      } catch {
        if (abortController.signal.aborted) return;
        setBatchFetchError(true);
        prefillSourceRef.current = 'error';
      } finally {
        if (!abortController.signal.aborted) setIsBatchDataLoading(false);
      }
    };

    loadBatchData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [dispatch, item.productionOperationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Derived values ---
  const quantityTarget = item.quantityTarget ?? 0;
  const quantityDelta = producedQuantity - quantityTarget;
  const progressPercent = quantityTarget > 0 ? (producedQuantity / quantityTarget) * 100 : 0;
  const perPieceCycleTime = producedQuantity > 0 ? Math.round(actualCycleTimeTotalInSeconds / producedQuantity) : 0;

  const isValid = producedQuantity >= 0 && actualSetupTimeInSeconds >= 0 && actualCycleTimeTotalInSeconds >= 0;

  // Job runs on an active batch: finishing won't take effect until the batch is finished (batch status
  // overrides ERP per the Job-Status-Determination wiki), so we defer the optimistic Finished flip.
  const isRunningOnActiveBatch = item.status === JOB_STATUSES.RUNNING && hasRunningBatch;
  const isRunningBatchUnknown = item.status === JOB_STATUSES.RUNNING && !hasRunningBatch && (isBatchDataLoading || batchFetchError);

  const provenanceText = useMemo(
    () => getProvenanceText(batchTimeData, batchFetchError ? 'error' : prefillSourceRef.current),
    [batchTimeData, batchFetchError]
  );

  const setupTimeRows: TimeRowData[] = useMemo(
    () => getSetupTimeRows(batchTimeData, actualSetupTimeInSeconds, fallbackSetupScheduled),
    [batchTimeData, actualSetupTimeInSeconds, fallbackSetupScheduled]
  );

  const cycleTimeRows: TimeRowData[] = useMemo(
    () => getCycleTimeRows(batchTimeData, actualCycleTimeTotalInSeconds, producedQuantity, fallbackCycleScheduled),
    [batchTimeData, actualCycleTimeTotalInSeconds, producedQuantity, fallbackCycleScheduled]
  );

  // --- Handlers ---
  const markDirty = useCallback(() => {
    if (!isDirty) setIsDirty(true);
  }, [isDirty]);

  // Auto-recalculate cycle time when quantity changes (if user hasn't manually edited it)
  const previousQuantityRef = useRef<number>(producedQuantity);

  // Scale cycle time proportionally to the new quantity. No-op when the user has edited cycle
  // time manually or when prevQty is 0 (avoids division by zero).
  const recalcCycleForQuantityChange = useCallback(
    (prevQty: number, nextQty: number) => {
      if (hasEditedActualCycle || prevQty <= 0) return;
      const perPieceRate = actualCycleTimeTotalInSeconds / prevQty;
      setActualCycleTimeTotalInSeconds(Math.round(perPieceRate * nextQty));
    },
    [hasEditedActualCycle, actualCycleTimeTotalInSeconds]
  );

  const updateQuantityAndRecalc = useCallback(
    (newQuantity: number) => {
      const prevQty = previousQuantityRef.current;

      setProducedQuantity(newQuantity);
      previousQuantityRef.current = newQuantity;

      recalcCycleForQuantityChange(prevQty, newQuantity);
    },
    [recalcCycleForQuantityChange]
  );

  const incrementQuantity = useCallback(() => {
    setProducedQuantity((prev) => {
      if (prev >= MAX_QUANTITY) return prev;
      const next = prev + 1;
      markDirty();

      recalcCycleForQuantityChange(prev, next);

      previousQuantityRef.current = next;
      return next;
    });
  }, [markDirty, recalcCycleForQuantityChange]);

  const decrementQuantity = useCallback(() => {
    setProducedQuantity((prev) => {
      if (prev <= 0) return prev;
      const next = prev - 1;
      markDirty();

      recalcCycleForQuantityChange(prev, next);

      previousQuantityRef.current = next;
      return next;
    });
  }, [markDirty, recalcCycleForQuantityChange]);

  const setQuantityDirect = useCallback(
    (n: number) => {
      const clamped = Math.max(0, Math.min(MAX_QUANTITY, Math.round(n)));
      updateQuantityAndRecalc(clamped);
      markDirty();
    },
    [updateQuantityAndRecalc, markDirty]
  );

  const handleSetActualEndDate = useCallback(
    (date: Date) => {
      setActualEndDate(date);
      markDirty();
    },
    [markDirty]
  );

  const handleSetActualSetupTime = useCallback(
    (seconds: number) => {
      setActualSetupTimeInSeconds(Math.max(0, Math.min(MAX_TIME_SECONDS, seconds)));
      setHasEditedActualSetup(true);
      markDirty();
    },
    [markDirty]
  );

  const handleSetActualCycleTime = useCallback(
    (seconds: number) => {
      setActualCycleTimeTotalInSeconds(Math.max(0, Math.min(MAX_TIME_SECONDS, seconds)));
      setHasEditedActualCycle(true);
      markDirty();
    },
    [markDirty]
  );

  // rows[0] is "Scheduled" when batches are linked, else "Paid" (the operation-level ERP estimate). Both
  // are the correct estimate baseline for over/under-time submit warnings.
  const scheduledSetupTimeInSeconds = setupTimeRows[0]?.value ?? 0;
  const scheduledCycleTimeTotalInSeconds = cycleTimeRows[0]?.value ?? 0;

  const performSubmit = async (skipOptimistic = false) => {
    setIsSubmitting(true);
    setSubmitError(null);

    const payload: FinishJobPayload = {
      productionOperationId: item.productionOperationId,
      actualEndDate: actualEndDate.toISOString(),
      actualQuantity: producedQuantity,
      actualSetupTimeInSeconds,
      actualCycleTimeTotalInSeconds,
      noteDetails: noteDetailsText,
    };

    const result = await dispatch(finishProductionOperation({ ...payload, machineId, skipOptimistic }));

    if (result.payload === true) {
      const machine = machines.find((m) => m.machineId === machineId);
      trackEvent(originScreen, {
        action: 'jobFinished',
        machineId: String(machineId || ''),
        machineName: (machine?.shortName || machine?.fullName || '') as string,
      });
      // Step 2: Upload note document if in rich mode (non-fatal — job already closed)
      if (editorMode === 'rich' && hasNote) {
        try {
          await uploadNote();
        } catch (error) {
          console.error('Note upload failed after job close:', error);
          // Draft is preserved by uploadNote on failure
        }
      } else {
        clearDraft();
      }
      // Deferred finish keeps the job Running (active batch) — skip the caller's optimistic local patch.
      if (!skipOptimistic) onFinishSuccess?.();
      hide();
    } else {
      setIsSubmitting(false);
      setSubmitError(i18n.t('Failed to finish job. Please try again.'));
    }
  };

  const handleSubmit = () => {
    if (!isValid || isSubmitting) return;

    // BE rule: actualEndDate must not be before the job's start date. Prefer the
    // actual start when the job has already begun, otherwise fall back to the schedule.
    const jobStartDate = item.actualStartDate ?? item.productionStartedOn ?? item.scheduledStartDate;

    const { errors, warnings } = getSubmitValidation({
      producedQuantity,
      quantityTarget,
      actualSetupTimeInSeconds,
      actualCycleTimeTotalInSeconds,
      scheduledSetupTimeInSeconds,
      scheduledCycleTimeTotalInSeconds,
      actualEndDate,
      jobStartDate,
    });

    // Surface only the first hard error to avoid alert spam — BE rejects on first violation anyway.
    if (errors.length > 0) {
      showErrorAlert(errors[0]);
      return;
    }

    // On an active batch, confirm via a blocking dialog and submit WITHOUT the optimistic flip so the
    // job doesn't falsely show Finished and revert on refresh; otherwise submit as usual.
    const proceed = isRunningOnActiveBatch
      ? () =>
          Alert({
            title: FINISH_ON_ACTIVE_BATCH_ALERT.title,
            text: FINISH_ON_ACTIVE_BATCH_ALERT.text,
            actions: [{ text: 'OK', style: 'primary', onPress: () => performSubmit(true) }],
          })
      : () => performSubmit(isRunningBatchUnknown);

    if (warnings.length > 0) {
      showWarningChain(warnings, 0, proceed);
      return;
    }

    proceed();
  };

  const handleCancel = useCallback(() => {
    if (!isDirty && !noteTouched) {
      clearDraft();
      hide();
      return;
    }

    Alert({
      title: 'Discard Changes?',
      text: 'You have unsaved changes. Are you sure you want to discard them?',
      actions: [
        { text: 'Keep Editing', style: 'primary' },
        {
          text: 'Discard',
          onPress: () => {
            clearDraft();
            hide();
          },
        },
      ],
      position: 'vertical',
      boldPrimary: true,
    });
  }, [isDirty, noteTouched, hide, clearDraft]);

  return {
    producedQuantity,
    actualEndDate,
    actualSetupTimeInSeconds,
    actualCycleTimeTotalInSeconds,
    isDirty,
    isSubmitting,
    isValid,
    quantityTarget,
    quantityDelta,
    progressPercent,
    perPieceCycleTime,
    setupTimeRows,
    cycleTimeRows,
    provenanceText,
    isBatchDataLoading,
    submitError,
    incrementQuantity,
    decrementQuantity,
    setQuantityDirect,
    setActualEndDate: handleSetActualEndDate,
    setActualSetupTime: handleSetActualSetupTime,
    setActualCycleTime: handleSetActualCycleTime,
    handleSubmit,
    handleCancel,
  };
};

export default useManageFinishJobModal;
